import classNames from 'classnames';

import { FilterMenu } from '../../filters/components/FilterMenu';
import {
  FilterTags,
  type FilterWithDisabledState,
} from '../../filters/components/FilterTags';
import { PrimaryFilter } from '../../filters/components/PrimaryFilter';
import { useTrialScreenerV2Defaults } from '../../filters/hooks/useTrialScreenerV2Defaults';
import { isDiscoveryPage } from '../../filters/utils/common';
import { isScreenerV2Page } from '../../utils/routing-helpers';
import { CustomDashActionBar } from '../CustomDashActionBar/CustomDashActionBar';
import { useEditMode } from '../EditModeProvider/EditModeProvider';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import previewStyles from '../SavedViews/saved-view-preview.module.css';
import { useSavedViewPreview } from '../SavedViews/savedViewPreviewStore';
import { EditableTabManager } from '../TabManager/EditableTabManager';
import { TabManager } from '../TabManager/TabManager';
import styles from './filter-bar.module.css';
import { useFilterTags } from './useFilterTags';

export const FilterBar = ({
  FilterTagsComponent,
  hidePrimaryFilterBar = false,
}: {
  FilterTagsComponent?: React.ReactNode;
  hidePrimaryFilterBar?: boolean;
}) => {
  const { config, revelioPageId } = useTabContentConfig();
  const { editMode } = useEditMode();
  const { primaryFilter } = config;
  // Discovery pages have no Snapshot/Over Time pill, so the analytics 54px
  // row allowance just reads as dead space around the 30px filter row.
  const isDiscoveryLayout =
    isDiscoveryPage(revelioPageId) || isScreenerV2Page(revelioPageId);

  const { filterTags, onRemoveFilter } = useFilterTags();
  // While a saved view is previewed, its filters stand in for the page's,
  // drawn by the same chips so the preview reads as the result will.
  const preview = useSavedViewPreview();
  const previewTags: FilterWithDisabledState[] = (preview?.lines ?? []).flatMap(
    (line) => (line.filter ? [{ filter: line.filter, disabled: false }] : [])
  );
  const shownTags = preview ? previewTags : filterTags;

  return (
    <div
      className={classNames(
        styles.filterBar,
        isDiscoveryLayout && styles.filterBarDiscovery
      )}
    >
      {primaryFilter && !hidePrimaryFilterBar && (
        <div className={styles.primaryRow}>
          <PrimaryFilter primaryFilter={primaryFilter} />
        </div>
      )}
      <div className={styles.secondaryRow}>
        <div
          className={classNames(
            styles.chipColumn,
            preview && previewStyles.besidePanel
          )}
          data-testid="non-primary-filter-chips"
        >
          {FilterTagsComponent || (
            <div className={styles.chipGroup}>
              <FilterTags
                filterTags={shownTags}
                onRemoveFilter={onRemoveFilter}
                isPreview={preview !== null}
              />
              <FilterMenu shiftLeft={shownTags.length > 0} />
            </div>
          )}
        </div>

        {/* Reserve the Snapshot/Over Time pill's height (26px tab + 2x2 padding)
            so single-tab pages — where TabManager renders nothing — don't shift
            the content below relative to pages that show the pill. */}
        <div className={styles.trailingRow}>
          <CustomDashActionBar />
          {editMode ? <EditableTabManager /> : <TabManager />}
        </div>
      </div>
    </div>
  );
};

const ScreenerV2FilterBarContent = () => {
  useTrialScreenerV2Defaults();
  return <FilterBar hidePrimaryFilterBar={true} />;
};

export const ScreenerV2FilterBar = () => <ScreenerV2FilterBarContent />;
